import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle, Save, ShieldCheck, History, LifeBuoy, Car } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Step1VerifyVAT from '../components/certifiedNetwork/Step1VerifyVAT';
import Step2StudioData from '../components/certifiedNetwork/Step2StudioData';
import Step3Compliance from '../components/certifiedNetwork/Step3Compliance';
import Step4Authorizations from '../components/certifiedNetwork/Step4Authorizations';
import Step5Training from '../components/certifiedNetwork/Step5Training';
import Step6Obligations from '../components/certifiedNetwork/Step6Obligations';
import Step7Review from '../components/certifiedNetwork/Step7Review';
import {
  CertifiedNetworkApplication,
  getApplication,
  updateApplication,
  submitApplication,
  saveAndSendLink
} from '../services/certifiedNetworkService';

/**
 * Pagina form multi-step per iscrizione Rete Certificata
 */
const CertifiedNetworkApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [editToken, setEditToken] = useState<string | null>(null);
  const [associateData, setAssociateData] = useState<any>(null);
  const [formData, setFormData] = useState<Partial<CertifiedNetworkApplication>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalSteps = 7;

  // Carica domanda da resume mode se presente
  useEffect(() => {
    const state = location.state as any;
    if (state?.applicationId && state?.resumeMode) {
      setApplicationId(state.applicationId);
      setEditToken(state.editToken || null);
      setCurrentStep(2); // Salta lo step di verifica P.IVA
      toast.success('Domanda ripresa con successo! Puoi continuare la compilazione.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [location]);

  // Carica domanda esistente se presente
  useEffect(() => {
    if (applicationId) {
      loadApplication();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [applicationId]);

  const loadApplication = async () => {
    if (!applicationId) return;
    
    setLoading(true);
    try {
      const response = await getApplication(applicationId);
      if (response.success) {
        setFormData(response.application);
      }
    } catch (error: any) {
      toast.error('Errore nel caricamento della domanda');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Salva automaticamente ad ogni cambio step
  const saveProgress = async (data: Partial<CertifiedNetworkApplication>) => {
    if (!applicationId) return;

    setSaving(true);
    try {
      await updateApplication(applicationId, data, editToken || undefined);
    } catch (error: any) {
      console.error('Errore salvataggio automatico:', error);
      // Non mostriamo errore all'utente per il salvataggio automatico
    } finally {
      setSaving(false);
    }
  };

  const handleStepComplete = async (stepData: any) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    // Salva progresso
    if (applicationId) {
      await saveProgress(updatedData);
    }

    // Vai allo step successivo
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!applicationId) {
      toast.error('ID domanda non trovato');
      return;
    }

    setLoading(true);
    try {
      const response = await submitApplication(applicationId, editToken || undefined);
      if (response.success) {
        toast.success('Domanda inviata con successo!');
        // Mostra pagina di conferma
        navigate('/rete-certificata/conferma', {
          state: { applicationId }
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Errore durante l\'invio della domanda';
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndSendLink = async () => {
    if (!applicationId) {
      toast.error('ID domanda non trovato');
      return;
    }

    // Blocca se la domanda è già approvata
    if (formData.status === 'approved') {
      toast.error('Non è possibile modificare una domanda già approvata');
      return;
    }

    if (!window.confirm('Vuoi salvare la domanda e ricevere un link via email per riprenderla in seguito?\n\nIl link sarà valido per 72 ore.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await saveAndSendLink(applicationId);
      if (response.success) {
        toast.success('Link inviato via email! Controlla la tua casella di posta.');
        navigate('/rete-certificata');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Errore durante l\'invio del link';
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const etichettaStep = PASSI[currentStep - 1];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-16">
      {/* Testata */}
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src="/images/rete-certificata-pagina/ingresso-studio-1280.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/50" />
        <div className="relative container mx-auto max-w-4xl px-4 pt-10 pb-28 md:pt-14">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 ring-1 ring-white/20 backdrop-blur-sm">
            <ShieldCheck className="h-4 w-4 text-primary-300" aria-hidden="true" />
            <span className="text-sm font-semibold text-white">Rete Certificata UNASCA</span>
          </div>
          <h1 className="mb-3 text-2xl md:text-4xl font-bold leading-tight tracking-tight text-white">
            Domanda di Adesione alla Rete Certificata UNASCA
          </h1>
          <p className="max-w-2xl text-sm md:text-base text-white/80">
            Compila tutti i campi richiesti per completare la tua domanda di iscrizione.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2 text-xs text-white/85">
            {[
              { testo: 'Salvataggio automatico a ogni step', Icona: Save },
              { testo: 'Riprendi quando vuoi', Icona: History },
              { testo: 'Assistenza dedicata', Icona: LifeBuoy },
            ].map(({ testo, Icona }) => (
              <li key={testo} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15">
                <Icona className="h-3.5 w-3.5 text-primary-300" aria-hidden="true" />
                {testo}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="container mx-auto max-w-4xl px-4">
        {/* Avanzamento: una strada con le tappe del modulo */}
        <div className="relative -mt-20 mb-6 rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-white">Step {currentStep} di {totalSteps}</span>
              <span className="text-gray-400"> — </span>
              {etichettaStep}
            </p>
            {saving && (
              <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600 dark:border-gray-600 dark:border-t-primary-400" aria-hidden="true" />
                Salvataggio...
              </span>
            )}
          </div>

          <div className="relative">
            {/* Strada con la parte percorsa in verde */}
            <div className="absolute left-[7.14%] right-[7.14%] top-4 h-2 -translate-y-1/2 rounded-full bg-gray-700" aria-hidden="true">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-500"
                style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
              />
              <div className="absolute inset-x-1 top-1/2 h-px -translate-y-1/2 border-t border-dashed border-white/60" />
            </div>
            <ol className="relative grid grid-cols-7" aria-label="Avanzamento della domanda">
              {PASSI.map((label, i) => (
                <StepLabel key={label} number={i + 1} current={currentStep} label={label} />
              ))}
            </ol>
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-6 rounded-3xl bg-white p-6 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 sm:p-8">
          {currentStep === 1 && (
            <Step1VerifyVAT
              onComplete={(data) => {
                setApplicationId(data.applicationId);
                setAssociateData(data.associate);
                handleStepComplete({});
              }}
            />
          )}

          {currentStep === 2 && (
            <Step2StudioData
              data={formData}
              associateData={associateData}
              onComplete={handleStepComplete}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <Step3Compliance
              data={formData}
              onComplete={handleStepComplete}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <Step4Authorizations
              data={formData}
              onComplete={handleStepComplete}
              onBack={handleBack}
            />
          )}

          {currentStep === 5 && (
            <Step5Training
              data={formData}
              applicationId={applicationId}
              onComplete={handleStepComplete}
              onBack={handleBack}
            />
          )}

          {currentStep === 6 && (
            <Step6Obligations
              data={formData}
              onComplete={handleStepComplete}
              onBack={handleBack}
            />
          )}

          {currentStep === 7 && (
            <Step7Review
              data={formData}
              associateData={associateData}
              onSubmit={handleSubmit}
              onBack={handleBack}
              loading={loading}
            />
          )}
        </div>

        {/* Help Text */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/40">
              <AlertCircle className="h-5 w-5 text-primary-700 dark:text-primary-400" aria-hidden="true" />
            </div>
            <div className="flex-1 text-sm text-gray-700 dark:text-gray-300">
              <p className="mb-2 text-base font-semibold text-gray-900 dark:text-white">Hai bisogno di aiuto?</p>
              <p className="mb-3">
                I tuoi progressi vengono salvati automaticamente ad ogni step.
                Puoi chiudere questa pagina e tornare in seguito inserendo nuovamente la tua Partita IVA.
              </p>
              <p className="mb-3">
                <strong className="text-gray-900 dark:text-white">Oppure</strong> clicca sul pulsante qui sotto per ricevere un link diretto via email
                che ti permetterà di riprendere la compilazione senza reinserire la Partita IVA.
                Il link sarà valido per 72 ore.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Per assistenza scrivici a{' '}
                <a
                  href="mailto:retecertificata@unasca.it"
                  className="font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800 dark:text-primary-400"
                >
                  retecertificata@unasca.it
                </a>
              </p>
            </div>
          </div>

          {/* Bottone Salva e Riprendi Dopo */}
          {applicationId && currentStep > 1 && formData.status !== 'approved' && (
            <div className="mt-5 flex justify-center border-t border-gray-100 pt-5 dark:border-gray-800">
              <button
                onClick={handleSaveAndSendLink}
                disabled={loading}
                className="flex items-center gap-2 rounded-full border-2 border-primary-600 px-6 py-2.5 font-semibold text-primary-700 transition-colors hover:bg-primary-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-300"
                title="Salva e ricevi link via email per riprendere dopo (valido 72h)"
              >
                <Save className="h-5 w-5" aria-hidden="true" />
                <span>Salva e Riprendi Dopo</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PASSI = ['Verifica', 'Dati Studio', 'Conformità', 'Autorizzazioni', 'Formazione', 'Obblighi', 'Riepilogo'];

// Componente helper per le label degli step
interface StepLabelProps {
  number: number;
  current: number;
  label: string;
}

const StepLabel: React.FC<StepLabelProps> = ({ number, current, label }) => {
  const isCompleted = number < current;
  const isCurrent = number === current;

  return (
    <li className="relative flex flex-col items-center gap-1.5" aria-current={isCurrent ? 'step' : undefined}>
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ring-4 transition-colors ${
          isCompleted
            ? 'bg-primary-600 text-white ring-white dark:ring-gray-900'
            : isCurrent
            ? 'bg-primary-600 text-white ring-primary-100 dark:ring-primary-900/60'
            : 'bg-white text-gray-500 ring-white border-2 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:ring-gray-900'
        }`}
      >
        {isCompleted ? <CheckCircle className="h-4 w-4" aria-hidden="true" /> : isCurrent ? <Car className="h-4 w-4" aria-hidden="true" /> : number}
        <span className="sr-only">{isCompleted ? `Step ${number} completato` : isCurrent ? `Step ${number}, in corso` : `Step ${number}`}</span>
      </div>
      <span
        className={`hidden text-center text-xs sm:block ${
          isCurrent ? 'font-semibold text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        {label}
      </span>
    </li>
  );
};

export default CertifiedNetworkApplicationPage;
